import { Injectable, Signal, computed, effect, inject, untracked } from '@angular/core';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
    IqEventBusQueueService,
} from '@dat/iq-event-bus';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { get, isEmpty, set, sortBy } from 'lodash';
import { Observable, filter } from 'rxjs';
import { IqMcEventBusPluginGraphqlService } from './iq-mc-event-bus-plugin-graphql.service';
import {
    McCondition,
    McLocation,
    McQuery,
    McResponseLt,
    McResponseLtSchema,
    createLtKey,
    createMcEventNames,
} from './iq-mc-event-bus-state.models';

interface State {
    conditions: Record<string, McCondition>;
    queries: Record<string, McQuery>;
}

@Injectable({
    providedIn: 'root',
})
export class IqMcEventBusStateLtService extends ComponentStore<State> {
    private _fetchKey: string = 'Lt';
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _registerMap: Record<string, IqEventBusBrokerHandler> = {};
    private _plugin: IqMcEventBusPluginGraphqlService = inject(
        IqMcEventBusPluginGraphqlService,
    );
    private _queue: IqEventBusQueueService = new IqEventBusQueueService();
    private _queries: McQuery[] = [];


    constructor() {
        super({ conditions: {}, queries: {} });

        /**
         * Log the state of the store when it changes
         * This is used for debugging and monitoring
         */
        // effect(() => {
        //     console.log('Lt State Updated');
        //     console.log(this.state());
        // });

        this._registerMap = this._bus.registerMap({
            [createMcEventNames(this._fetchKey)]: {
                callback: ({ query }: { query: McQuery }) => {
                    if (!query) return;
                    this.addQuery(query);
                    this.fetchLt(query);
                },
            },
            [createMcEventNames(this._fetchKey, ['update-query'])]: {
                callback: ({ query }: { query: McQuery }) => {
                    if (!query) return;
                    this.patchQuery(query);
                    this.fetchLt(this.getQueryById(query?.id)());
                },
            },
        });
    }

    //#region Updaters
    addQuery = this.updater((state: State, query: McQuery) => {
        const id = query?.id ?? undefined;
        const currentQuery = get(state, ['queries', id], {});

        return !!id ? {
            ...state,
            queries: {
                ...state.queries,
                [id]: {
                    ...currentQuery,
                    ...query,
                },
            },
        } : state;
    });
    patchQuery = this.updater((state: State, query: Partial<McQuery>) => {
        const id = query?.id ?? undefined;
        const currentQuery = get(state, ['queries', id], {});

        if (isEmpty(currentQuery)) return state;

        return !!id ? {
            ...state,
            queries: {
                ...state.queries,
                [id]: {
                    ...currentQuery,
                    ...query,
                },
            },
        } : state;
    });

    setLtFetchGeo = this.updater((state: State, query: McQuery) => {
        if (!query.geoCode?.length) return state;

        const conditionKey = createLtKey(
            query.equipmentType,
            query.geoType,
            query.timeframe,
        );
        const fetchInMs = Date.now();
        const geoKey = query.geoCode;

        return {
            ...state,
            conditions: {
                ...state.conditions,
                [conditionKey]: {
                    id: conditionKey,
                    Lt: {
                        ...(state.conditions[conditionKey]?.Lt ?? {}),
                        [geoKey]: {
                            ...McResponseLtSchema.parse({
                                fetchInMs,
                                fetch: true,
                                code: query.geoCode,
                                type: query.geoType,
                                equipmentType: query.equipmentType,
                                timeframe: query.timeframe,
                            }),
                        },
                    },
                },
            },
        };
    });

    setLtList = this.updater((state: State, ltList: McResponseLt[]) => {
        return ltList.reduce((acc, lt) => {
            const conditionKey = createLtKey(
                lt.equipmentType,
                lt.type,
                lt.timeframe,
            );
            const geoKey = lt.code;
            const currentLt = acc.conditions[conditionKey]?.Lt?.[geoKey] ?? {};
            const condition: McCondition = acc.conditions[conditionKey] ?? {};
            const fetchInMs = Date.now();
            return {
                ...acc,
                conditions: {
                    ...acc.conditions,
                    [conditionKey]: {
                        id: conditionKey,
                        ...condition,
                        Lt: {
                            ...(acc.conditions[conditionKey]?.Lt ?? {}),
                            [geoKey]: {
                                ...currentLt,
                                ...lt,
                                fetch: currentLt.fetch ?? true,
                                fetchInMs: currentLt.fetchInMs ?? fetchInMs,
                                durationInMs:
                                    lt.loadedInMs - currentLt.fetchInMs,
                            },
                        },
                    },
                },
            };
        }, state);
    });

    setLtTimeline = this.updater((state: State, ltRecord: McResponseLt) => {
        if (
            !ltRecord?.equipmentType ||
            !ltRecord?.type ||
            !ltRecord?.timeframe ||
            !ltRecord?.code
        )
        return state;
        const conditionKey = createLtKey(
            ltRecord.equipmentType,
            ltRecord.type,
            ltRecord.timeframe,
        );
        const geoKey = ltRecord.code;
        const currentLt = state.conditions[conditionKey]?.Lt?.[geoKey] ?? {};
        const currentCondition = state.conditions[conditionKey] ?? {};
        return {
            ...state,
            conditions: {
                ...state.conditions,
                [conditionKey]: {
                    ...currentCondition,
                    id: conditionKey,
                    Lt: {
                        ...(state.conditions[conditionKey]?.Lt ?? {}),
                        [geoKey]: {
                            ...currentLt,
                            fetch: currentLt.fetch ?? true,
                            timeline: ltRecord.timeline,
                            fetchInMs: currentLt.fetchInMs,
                            durationInMs:
                                ltRecord.loadedInMs - currentLt.fetchInMs,
                        },
                    },
                },
            },
        };
    });

    setLtFetchArea = this.updater((state: State, query: McQuery) => {
        const conditionKey = createLtKey(
            query.equipmentType,
            query.geoType,
            query.timeframe,
        );
        const fetchInMs = Date.now();
        const condition = state.conditions[conditionKey] ?? {};

        return {
            ...state,
            conditions: {
                ...state.conditions,
                [conditionKey]: {
                    ...condition,
                    id: conditionKey,
                    LtAreaResponseInfo: McResponseLtSchema.parse({
                        ...condition.LtAreaResponseInfo,
                        fetch: true,
                        fetchInMs,
                        type: query.geoType,
                        equipmentType: query.equipmentType,
                        timeframe: query.timeframe,
                    }),
                },
            },
        };
    });

    setLtLoadArea = this.updater((state: State, query: McQuery) => {
        const conditionKey = createLtKey(
            query.equipmentType,
            query.geoType,
            query.timeframe,
        );
        const loadedInMs = Date.now();
        const condition = state.conditions[conditionKey] ?? {};
        const responseInfo = condition.LtAreaResponseInfo ?? {};
        const durationInMs = !isNaN(loadedInMs - responseInfo.fetchInMs)
            ? loadedInMs - responseInfo.fetchInMs
            : -1;
        return {
            ...state,
            conditions: {
                ...state.conditions,
                [conditionKey]: {
                    ...condition,
                    id: conditionKey,
                    LtAreaResponseInfo: McResponseLtSchema.parse({
                        ...responseInfo,
                        loadedInMs: Date.now(),
                        loaded: true,
                        durationInMs,
                    }),
                },
            },
        };
    });

    setLtToEmpty = this.updater((state: State, query: McQuery) => {
        const conditionKey: string = createLtKey(
            query.equipmentType,
            query.geoType,
            query.timeframe,
        );
        const geoKey = query.geoCode;
        const currentLt = state.conditions[conditionKey]?.Lt?.[geoKey] ?? {};
        const loadedInMs: number = Date.now();
        return {
            ...state,
            conditions: {
                ...state.conditions,
                [conditionKey]: {
                    id: conditionKey,
                    Lt: {
                        ...(state.conditions[conditionKey]?.Lt ?? {}),
                        [geoKey]: {
                            ...currentLt,
                            hasError: true,
                            error: 'No Data',
                            loaded: true,
                            loadedInMs,
                            fetch: currentLt.fetch,
                            fetchInMs: currentLt.fetchInMs,
                            durationInMs: loadedInMs - currentLt.fetchInMs,
                        },
                    },
                },
            },
        };

    });

    clearLtState = this.updater(() => {
        return {
            conditions: {},
            queries: {},
        }
    });

    //#endregion

    //#region Effects

    initialize = this.effect(($trigger) => {
        return $trigger;
    });

    fetchLt = this.effect(($query: Observable<McQuery>) => {
        return $query.pipe(
            tapResponse({
                next: async (query: McQuery) => {
                    // Since we don't have L/T for Forecast, we set the LT list for forecast to empty without making a call to facade
                    if(query.timeframe === 'EIGHT_DAY_FORECAST'){
                        this.fetchLtForecastError(query);
                        return;
                    }
                    //Fetch lt ratios for all geographies
                    if (!this.isAreaFetched(query)()) {
                        this.setLtFetchArea(query);
                        const lt: McResponseLt[] =
                            await this._getAllLtRatios(query);
                        this.setLtList(lt);
                        this.setLtLoadArea(query);
                    }

                    // We make the next api call only if we need to fetch the timeline
                    if (this.isTimelineRequired(query)) {
                        // Fetch the timeline for specific geo
                        this.fetchLtTimeline(query);
                        return;
                    }
                    this._queries.push(query);
                    const sendLtDataEvents = computed(() => {
                        if(this.isAreaLoaded(query)()) {
                            untracked(() => {
                                this._queries.forEach((query) => {
                                    this.sendLtEvent(query?.id);
                                });
                                this._queries = []
                            });
                        }
                    });
                    sendLtDataEvents();
                },
                error: (error) => {
                    console.error(error);
                },
            }),
            tapResponse({
                next: (query: McQuery) => {
                    this.sendLtEvent(query?.id);
                },
                error: (e) => {
                    console.error(e);
                },
            }),
        );
    });


    fetchLtTimeline = this.effect(($query: Observable<McQuery>) => {
        return $query.pipe(
            filter((query) => !!query.geoCode?.length),
            tapResponse({
                next: async (query: McQuery) => {
                    if (this.isTimelineFetched(query)()) return;
                    const timeline: McResponseLt =
                        await this._fetchTimeline(query);
                    this.setLtTimeline(timeline);
                    this.sendLtEvent(query?.id);
                },
                error: (error) => {
                    console.error(error);
                },
            }),
        );
    });

    fetchLtForecastError = this.effect(($query: Observable<McQuery>) => {
        return $query.pipe(
            tapResponse({
                next: (query: McQuery) => {
                    this.setLtFetchArea(query);
                    this.setLtToEmpty(query);
                    this.setLtLoadArea(query);
                    this.sendLtEvent(query?.id);
                },
                error: (error) => {
                    console.error(error);
                },
            }),
        )
    });

    sendLtEvent = this.effect((queryId: Observable<string>) => {
        return queryId.pipe(
            filter((queryId) => !!queryId),
            filter((queryId) => !!this.getQueryById(queryId)()),
            filter((queryId) => {
                const query = this.getQueryById(queryId)();
                return query?.type === 'area';
            }),
            tapResponse({
                next: (queryId: string) => {
                    const query = this.getQueryById(queryId)();
                    const lt = this.getLtMap(query)();
                    const payload = {
                        query,
                        lt,
                        ltResponse:
                            this.getLtArea(query)()?.LtAreaResponseInfo ?? {},
                    };

                    /**
                     * Debug Message
                     */
                    // console.log(
                    //     createMcEventNames(this._fetchKey, ['data']),
                    //     payload,
                    // );
                    this._bus.dispatch({
                        name: createMcEventNames(this._fetchKey, ['data']),
                        payload,
                    });
                },
                error: (error) => {
                    console.error(error);
                },
            }),
        );
    });

    //#endregion

    //#region Selectors

    getQueryById = (id: string): Signal<McQuery> =>
        this.selectSignal((state) => {
            return state.queries[id];
        });

    getLtByGeo = (query: McQuery): Signal<McResponseLt> =>
        this.selectSignal((state) => {
            const conditionKey = createLtKey(
                query.equipmentType,
                query.geoType,
                query.timeframe,
            );
            const geoKey = query.geoCode;

            return state.conditions[conditionKey]?.Lt?.[geoKey];
        });

    getLtArea = (query: McQuery): Signal<McCondition> =>
        this.selectSignal((state) => {
            const conditionKey = createLtKey(
                query.equipmentType,
                query.geoType,
                query.timeframe,
            );
            return state.conditions[conditionKey];
        });

    getLtMap = (query: McQuery): Signal<Record<string, McResponseLt>> =>
        this.selectSignal((state) => {
            const conditionKey = createLtKey(
                query.equipmentType,
                query.geoType,
                query.timeframe,
            );
            const lt = state.conditions[conditionKey]?.Lt ?? {};

            return Object.entries(lt).reduce((acc, [key, value]) => {
                return set(acc, key, value);
            }, {});
        });

    getLtCondition = (query: McQuery): Signal<McCondition> =>
        this.selectSignal((state) => {
            const conditionKey = createLtKey(
                query.equipmentType,
                query.geoType,
                query.timeframe,
            );
            return state.conditions[conditionKey] ?? {};
        });

    isAreaFetched = (query: McQuery): Signal<boolean> =>
        this.selectSignal(this.getLtArea(query), (Lt: McCondition) => {
            return !!Lt?.LtAreaResponseInfo.fetch;
        });

    isAreaLoaded = (query: McQuery): Signal<boolean> => 
        this.selectSignal(this.getLtArea(query), (Lt: McCondition) => {
            return !!Lt?.LtAreaResponseInfo.loaded;
            });  

    isTimelineFetched = (query: McQuery): Signal<boolean> => {
        return this.selectSignal(this.getLtByGeo(query), (lt: McResponseLt) => {
            return !isEmpty(lt?.timeline);
        });
    };

    isGeoFetched = (query: McQuery): Signal<boolean> =>
        this.selectSignal(this.getLtByGeo(query), (lt: McResponseLt) => {
            return !!lt?.fetch;
        });

    readonly queryList: Signal<McQuery[]> = this.selectSignal<McQuery[]>(
        (state): McQuery[] => sortBy(Object.values(state.queries), 'id'),
    );

    readonly geLtByGeoById = (id: string): Signal<McResponseLt> =>
        this.selectSignal((state) => {
            const query = state.queries[id];
            if (!query) return null;

            const conditionKey = createLtKey(
                query.equipmentType,
                query.geoType,
                query.timeframe,
            );
            const geoKey = query.geoCode;

            return state.conditions[conditionKey]?.Lt?.[geoKey];
        });

    //#endregion

    //#region Helpers
    private async _getAllLtRatios(query: McQuery): Promise<McResponseLt[]> {
        const queryWithoutGeography = {
            ...query,
            geoCode: null,
            displayName: null,
        };
        return await this._plugin.getLt(queryWithoutGeography);
    }

    private async _fetchTimeline(
        query: McQuery,
    ): Promise<Partial<McResponseLt>> {
        const ltRecord = await this._plugin.getLtTimeline(query);
        return ltRecord?.[0];
    }

    isTimelineRequired(query: McQuery): boolean {
        return (
            !!query.geoCode?.length &&
            (query?.timeframe === 'THIRTY_DAYS_BACK' ||
                query?.timeframe === 'EIGHT_DAYS_BACK')
        );
    }
    //#endregion
}
